<template>
  <div class="sci-page-wrapper" style="font-family: 'Inter', sans-serif; background: #f4f6f9; min-height: 100vh;">
    <div class="max-w-5xl mx-auto px-4 pt-24 pb-6">

      <!-- VOLTAR -->
      <button @click="$router.back()" class="flex items-center gap-2 text-slate-500 hover:text-orange-500 transition-colors mb-6 group">
        <v-icon small color="inherit" class="group-hover:-translate-x-1 transition-transform">mdi-arrow-left</v-icon>
        <span class="text-[10px] font-bold uppercase tracking-widest">Voltar para listagem</span>
      </button>

      <v-form ref="form" v-model="valid" lazy-validation>
        <div class="space-y-6 pb-24">

          <!-- SEÇÃO 1: IDENTIDADE E TIPO -->
          <section class="bg-white rounded-xl overflow-hidden shadow-sm border border-slate-100">
            <div class="px-6 py-3 flex items-center gap-3" style="background-color: #333333;">
              <v-icon color="orange" small>mdi-image-outline</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">IDENTIDADE E TIPO</h2>
            </div>

            <div class="p-6">
              <div class="flex flex-col md:flex-row gap-8 items-center md:items-start">
                <!-- LOGO -->
                <div class="flex flex-col items-center gap-2 shrink-0">
                  <div
                    class="w-32 h-32 rounded-lg border-2 border-slate-100 bg-slate-50 overflow-hidden cursor-pointer hover:border-orange-400 transition-colors flex items-center justify-center p-4"
                    @click="$refs.fileInput.click()"
                  >
                    <img
                      v-if="data.provider_logo"
                      :src="'https://api.sigfrotagov.com.br/uploads/' + data.provider_logo"
                      class="w-full h-full object-contain"
                    />
                    <v-icon v-else color="#cbd5e0" size="40">mdi-image-plus</v-icon>
                  </div>
                  <input type="file" accept="image/jpeg, image/png" @change="uploadImage" ref="fileInput" style="display:none" />
                  <span class="text-[9px] font-bold text-slate-400 uppercase tracking-widest cursor-pointer hover:text-orange-500 transition-colors" @click="$refs.fileInput.click()">ALTERAR LOGOTIPO</span>
                </div>

                <!-- TIPO E VÍNCULO -->
                <div class="flex-1 w-full">
                  <v-row dense>
                    <v-col cols="12">
                      <div 
                        class="p-4 rounded-lg border flex items-center justify-between cursor-pointer transition-all mb-4"
                        :class="data.provider_r_type ? 'bg-slate-800 border-slate-800 text-white' : 'bg-slate-50 border-slate-200 text-slate-600'"
                        @click="data.provider_r_type = !data.provider_r_type"
                      >
                        <div class="flex items-center gap-3">
                          <v-icon :color="data.provider_r_type ? 'orange' : 'slate-400'" small>{{ data.provider_r_type ? 'mdi-check-decagram' : 'mdi-decagram-outline' }}</v-icon>
                          <div>
                            <p class="text-[10px] font-bold uppercase tracking-widest mb-0">Tipo de Fornecedor</p>
                            <p class="text-[9px] uppercase mb-0 opacity-60">{{ data.provider_r_type ? 'Pessoa Jurídica (PJ)' : 'Pessoa Física (PF)' }}</p>
                          </div>
                        </div>
                        <v-switch v-model="data.provider_r_type" color="orange" inset hide-details class="mt-0" @click.stop />
                      </div>
                    </v-col>
                    
                    <v-col cols="12">
                      <label class="field-label">CATEGORIA DE ESPECIALIZAÇÃO</label>
                      <v-autocomplete 
                        v-model="data.attendance_id" 
                        :items="attendances" 
                        item-text="attendance_desc" 
                        item-value="attendance_id" 
                        placeholder="SELECIONE O TIPO..." 
                        outlined dense hide-details 
                        class="field-input" 
                        prepend-inner-icon="mdi-briefcase-check-outline"
                      />
                    </v-col>
                  </v-row>
                </div>
              </div>
            </div>
          </section>

          <!-- SEÇÃO 2: DADOS CADASTRAIS -->
          <section class="bg-white rounded-xl overflow-hidden shadow-sm border border-slate-100">
            <div class="px-6 py-3 flex items-center gap-3" style="background-color: #333333;">
              <v-icon color="orange" small>mdi-card-account-details-outline</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">DADOS CADASTRAIS</h2>
            </div>

            <div class="p-6">
              <v-row dense>
                <v-col cols="12">
                  <label class="field-label">{{ data.provider_r_type ? 'RAZÃO SOCIAL' : 'NOME COMPLETO' }}</label>
                  <v-text-field v-model="data.provider_name" placeholder="EX: POSTO CENTRAL LTDA" outlined dense hide-details class="field-input font-bold" />
                </v-col>
                
                <v-col cols="12" md="6">
                  <label class="field-label">{{ data.provider_r_type ? 'CNPJ' : 'CPF' }}</label>
                  <v-text-field 
                    v-model="data.provider_document" 
                    @input="data.provider_r_type ? setCnpj(data.provider_document) : setCpf(data.provider_document)"
                    :placeholder="data.provider_r_type ? '00.000.000/0000-00' : '000.000.000-00'"
                    outlined dense hide-details 
                    class="field-input"
                    :error="(data.provider_r_type ? !validCnpj(data.provider_document) : !validCpf(data.provider_document)) && data.provider_document"
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <label class="field-label">{{ data.provider_r_type ? 'INSCRIÇÃO ESTADUAL' : 'RG' }}</label>
                  <v-text-field v-model="data.provider_register" placeholder="NÚMERO DO REGISTRO" outlined dense hide-details class="field-input" />
                </v-col>
              </v-row>
            </div>
          </section>

          <!-- SEÇÃO 3: CONTATO E LOCALIZAÇÃO -->
          <section class="bg-white rounded-xl overflow-hidden shadow-sm border border-slate-100">
            <div class="px-6 py-3 flex items-center gap-3" style="background-color: #333333;">
              <v-icon color="orange" small>mdi-map-marker-radius-outline</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">CONTATO E LOCALIZAÇÃO</h2>
            </div>

            <div class="p-6">
              <v-row dense>
                <v-col cols="12" md="4">
                  <label class="field-label">CELULAR / WHATSAPP</label>
                  <v-text-field v-model="data.provider_cel" v-mask-phone.br placeholder="(00) 00000-0000" outlined dense hide-details class="field-input" />
                </v-col>
                <v-col cols="12" md="4">
                  <label class="field-label">TELEFONE FIXO</label>
                  <v-text-field v-model="data.provider_tel" v-mask-phone.br placeholder="(00) 0000-0000" outlined dense hide-details class="field-input" />
                </v-col>
                <v-col cols="12" md="4">
                  <label class="field-label">E-MAIL</label>
                  <v-text-field v-model="data.provider_mail" type="email" placeholder="email@fornecedor.com" outlined dense hide-details class="field-input lowercase" />
                </v-col>

                <v-col cols="12" md="3" class="mt-4">
                  <label class="field-label">CEP</label>
                  <v-text-field v-model="data.provider_postal_code" v-mask-cep @blur="getCep(data.provider_postal_code)" placeholder="00000-000" outlined dense hide-details class="field-input" />
                </v-col>
                <v-col cols="12" md="7" class="mt-4">
                  <label class="field-label">ENDEREÇO</label>
                  <v-text-field v-model="data.provider_street" placeholder="RUA, AVENIDA, ETC" outlined dense hide-details class="field-input" />
                </v-col>
                <v-col cols="12" md="2" class="mt-4">
                  <label class="field-label">NÚMERO</label>
                  <v-text-field v-model="data.provider_number" placeholder="S/N" outlined dense hide-details class="field-input text-center" />
                </v-col>

                <v-col cols="12" md="5" class="mt-4">
                  <label class="field-label">BAIRRO</label>
                  <v-text-field v-model="data.provider_district" placeholder="BAIRRO" outlined dense hide-details class="field-input" />
                </v-col>
                <v-col cols="12" md="5" class="mt-4">
                  <label class="field-label">CIDADE</label>
                  <v-text-field v-model="data.provider_city" placeholder="CIDADE" outlined dense hide-details class="field-input" />
                </v-col>
                <v-col cols="12" md="2" class="mt-4">
                  <label class="field-label">UF</label>
                  <v-text-field v-model="data.provider_state" maxlength="2" placeholder="UF" outlined dense hide-details class="field-input text-center" />
                </v-col>
              </v-row>
            </div>

            <!-- FOOTER AÇÕES -->
            <div class="bg-slate-50 px-6 py-4 border-t border-slate-100 flex justify-end items-center gap-4">
              <button
                type="button"
                @click="$router.back()"
                style="background: none; border: none; cursor: pointer; white-space: nowrap;"
                class="text-slate-400 hover:text-slate-600 font-bold text-[11px] uppercase tracking-widest transition-colors"
              >
                CANCELAR
              </button>
              <button
                type="button"
                @click="validate"
                :disabled="loader"
                style="background-color: #f37021; color: white; display: inline-flex; align-items: center; gap: 8px; border-radius: 8px; padding: 0 24px; font-size: 11px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; border: none; cursor: pointer; height: 42px; width: auto; flex-shrink: 0;"
              >
                <v-icon v-if="loader" small class="animate-spin" color="white">mdi-loading</v-icon>
                <v-icon v-else small color="white">mdi-check</v-icon>
                <span>{{ id ? 'SALVAR ALTERAÇÕES' : 'CONFIRMAR CADASTRO' }}</span>
              </button>
            </div>
          </section>
        </div>
      </v-form>
    </div>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snack.show"
      :color="snack.color"
      timeout="4000"
      top
      elevation="24"
      class="custom-snack"
    >
      <div class="flex items-center gap-3">
        <v-icon color="white" small>{{ snack.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
        <span class="text-xs font-bold uppercase tracking-widest">{{ snack.message }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn dark icon v-bind="attrs" @click="snack.show = false">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  props: ["id"],
  data: () => ({
    valid: false,
    loader: false,
    attendances: [],
    snack: {
      show: false,
      message: "",
      color: "success",
    },
    data: {
      provider_id: null,
      attendance_id: null,
      provider_logo: "",
      provider_type: "",
      provider_name: "",
      provider_document: "",
      provider_register: "",
      provider_im: "",
      provider_mail: "",
      provider_cel: "",
      provider_tel: "",
      provider_postal_code: "",
      provider_street: "",
      provider_number: "",
      provider_complement: "",
      provider_district: "",
      provider_city: "",
      provider_state: "",
      provider_r_type: true,
    },
  }),
  created() {
    this.$http.get("/settings/attendance_types").then((r) => (this.attendances = r.data));
    if (this.id) {
      this.$store.dispatch("setLoader", true);
      this.$http.get(`providers/${this.id}`).then((r) => {
        this.data = r.data[0];
        delete this.data.created_at;
        this.$store.dispatch("setLoader", false);
      });
    }
  },
  methods: {
    setCnpj(v) {
      if (!v) return;
      v = v.replace(/\D/g, "");
      v = v.replace(/^(\d{2})(\d)/, "$1.$2");
      v = v.replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3");
      v = v.replace(/\.(\d{3})(\d)/, ".$1/$2");
      v = v.replace(/(\d{4})(\d)/, "$1-$2");
      this.data.provider_document = v;
    },
    setCpf(v) {
      if (!v) return;
      v = v.replace(/\D/g, "");
      v = v.replace(/(\d{3})(\d)/, "$1.$2");
      v = v.replace(/(\d{3})(\d)/, "$1.$2");
      v = v.replace(/(\d{3})(\d{1,2})$/, "$1-$2");
      this.data.provider_document = v;
    },
    validCnpj(v) {
      if (!v) return false;
      let cnpj = v.replace(/\D/gim, "");
      if (cnpj.length !== 14) return false;
      if (/^(\d)\1+$/.test(cnpj)) return false;
      let t = cnpj.length - 2, d = cnpj.substring(t), d1 = parseInt(d.charAt(0)), d2 = parseInt(d.charAt(1)),
        calc = (x) => {
          let n = cnpj.substring(0, x), y = x - 7, s = 0;
          for (let i = x; i >= 1; i--) { s += n.charAt(x - i) * y--; if (y < 2) y = 9; }
          let r = 11 - (s % 11); return r > 9 ? 0 : r;
        };
      return calc(t) === d1 && calc(t + 1) === d2;
    },
    validCpf(cpf) {
      if (!cpf) return false;
      cpf = cpf.replace(/\D/g, "");
      if (cpf.toString().length != 11 || /^(\d)\1{10}$/.test(cpf)) return false;
      var result = true;
      [9, 10].forEach(function (j) {
        var soma = 0, r;
        cpf.split(/(?=)/).splice(0, j).forEach(function (e, i) { soma += parseInt(e) * (j + 2 - (i + 1)); });
        r = soma % 11; r = r < 2 ? 0 : 11 - r; if (r != cpf.substring(j, j + 1)) result = false;
      });
      return result;
    },
    validate() {
      if (!this.data.provider_name || !this.data.provider_document) {
        this.snack.show = true; this.snack.color = "warning"; this.snack.message = "Atenção: Preencha o Nome e CPF/CNPJ!"; return;
      }
      this.createOrUpdate();
    },
    createOrUpdate() {
      this.loader = true;
      this.$store.dispatch("setLoader", true);
      const method = this.id ? 'put' : 'post';
      const url = this.id ? `/providers/${this.id}` : "/providers";
      this.$http[method](url, this.data).then((r) => {
        this.snack.show = true; this.snack.message = r.data.result; this.snack.color = "success";
        setTimeout(() => this.$router.push("/providers-manager"), 1500);
      }).catch((e) => {
        this.snack.show = true; this.snack.message = e.message; this.snack.color = "error";
        this.loader = false; this.$store.dispatch("setLoader", false);
      });
    },
    uploadImage(event) {
      if (!event.target.files[0]) return;
      this.$store.dispatch("setLoader", true);
      const fd = new FormData(); fd.append("image", event.target.files[0], event.target.files[0].name);
      this.$http.post("/upload", fd).then((r) => {
        this.data.provider_logo = r.data.cover; this.$store.dispatch("setLoader", false);
      }).catch((e) => {
        alert("Erro ao realizar upload da imagem! " + e.message); this.$store.dispatch("setLoader", false);
      });
    },
    getCep(cep) {
      if (cep && cep.length >= 8) {
        this.loader = true;
        let c = cep.replace(/\D/g, "");
        this.$http.get("https://viacep.com.br/ws/" + c + "/json/").then((response) => {
          if (!response.data.erro) {
            this.data.provider_street = response.data.logradouro;
            this.data.provider_district = response.data.bairro;
            this.data.provider_city = response.data.localidade;
            this.data.provider_state = response.data.uf;
          }
          this.loader = false;
        }).catch(() => { this.loader = false; });
      }
    },
  },
};
</script>

<style scoped>
.field-label {
  display: block;
  font-size: 9px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 4px;
}

.field-input >>> .v-input__control .v-input__slot {
  background-color: #f8fafc !important;
  border-radius: 6px !important;
}

.field-input >>> .v-input__control .v-input__slot fieldset {
  border-color: #e2e8f0 !important;
  border-width: 1px !important;
}

.field-input >>> .v-input__control .v-input__slot:hover fieldset {
  border-color: #f37021 !important;
}

.field-input >>> input,
.field-input >>> .v-select__selection {
  font-size: 13px !important;
  color: #334155 !important;
}

.custom-snack >>> .v-snack__wrapper {
  border-radius: 10px !important;
}

@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.animate-spin { animation: spin 1s linear infinite; }
</style>
